import React, { useState } from 'react';
import MarketingLayout from './Layouts/MarketingLayout';
import { Link } from '@inertiajs/react';

const fallbackSolutions = [
  {
    id: 'results',
    label: 'Results & Reports',
    slug: 'results-reports',
    overview: 'End result processing chaos, Excel chasing, and last-minute errors.',
    icon: 'fas fa-file-invoice',
    feature_highlights: [
      'Teachers upload grades directly into a secure portal',
      'Management reviews and approves gradebooks with one click',
      'Auto-release reports to parents via Student Portal, Email, or WhatsApp',
      'Withhold results automatically for outstanding fees or pending approvals',
    ],
  },
  {
    id: 'fees',
    label: 'Fees & Payments',
    slug: 'fees-payments',
    overview: 'See every payment in real time, reduce leakage, and end balance disputes.',
    icon: 'fas fa-wallet',
    feature_highlights: [
      'Configure fee structures by class, term, or learner',
      'Track paid, partial, and outstanding balances from one ledger',
      'Give finance teams cleaner reconciliation and approval trails',
    ],
  },
  {
    id: 'cbt',
    label: 'CBT & Exams',
    slug: 'cbt-exams',
    overview: 'Run digital exams confidently, even in low-connectivity classrooms.',
    icon: 'fas fa-laptop-code',
    feature_highlights: [
      'Create timed assessments with reusable question banks',
      'Support controlled exam delivery for school computer labs',
      'Sync scores back into academic workflows after grading',
    ],
  },
];

const stripHtml = (html) => {
  if (!html) {
    return '';
  }

  if (typeof window === 'undefined') {
    return String(html).replace(/<[^>]*>/g, '');
  }

  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  return doc.body.textContent || '';
};

const institutionImages = {
  k12: '/images/sections/originals/academics_1547200287.jpg',
  vocational_school: '/images/sections/originals/facilities_1547199816.jpg',
  monotechnic: '/images/sections/originals/academics_1547197961.jpg',
  polytechnic: '/images/sections/originals/facilities_1547201971.jpg',
  university: '/images/sections/originals/gallery_1547198944.jpg',
  business_school: '/images/people/stephen-longstaff_1623116564.png',
  islamic_school: '/images/sections/originals/academics_1547199453.jpg',
  bible_school: '/images/sections/originals/vision_1548936113.jpg',
  special_school: '/images/sections/originals/extra-curricular_1547200495.jpg',
};

export default function Home({ publishedSolutions = [], supportedInstitutionTypes = [] }) {
  const [activeSlide, setActiveSlide] = useState(0);

  const catalogSolutions = publishedSolutions.length > 0 ? publishedSolutions : fallbackSolutions;
  const slides = catalogSolutions.map((solution) => {
    const overview = stripHtml(solution.overview);
    const features = (solution.feature_highlights || []).filter(Boolean);

    return {
      id: solution.id,
      title: solution.label,
      tagline: overview || 'A focused SchoolPlex solution for improving one operational workflow without forcing a full-platform rollout.',
      points: features.length > 0
        ? features
        : [
            'Activate this workflow without disrupting the rest of the school',
            'Give staff a clearer process with better approval visibility',
            'Connect the solution to the wider SchoolPlex platform when ready',
          ],
      icon: solution.icon || 'fas fa-cubes',
      startLink: `/onboarding?solutions=${encodeURIComponent(solution.slug)}`,
      learnLink: `/solutions/${solution.slug}`,
    };
  });
  const selectedSolution = slides[activeSlide] || slides[0];
  const institutionTypes = supportedInstitutionTypes.length > 0
    ? supportedInstitutionTypes
    : [
        {
          code: 'k12',
          name: 'K12',
          description: 'Institution focused on early-years, primary, junior-secondary, and senior-secondary education.',
        },
        {
          code: 'vocational_school',
          name: 'Vocational School',
          description: 'Institution focused on vocational, technical, skills, and trade pathways.',
        },
        {
          code: 'polytechnic',
          name: 'Polytechnic',
          description: 'Tertiary institution offering technical, professional, diploma, and higher diploma programs.',
        },
        {
          code: 'university',
          name: 'University',
          description: 'Degree-awarding tertiary institution with undergraduate and postgraduate programs.',
        },
      ];

  return (
    <MarketingLayout title="Education Intelligence Platform">
      {/* Hero Section */}
      <section className="spx-hero">
        <div className="spx-container animate-fade-in">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '9999px', backgroundColor: 'var(--accent-blue-light)', color: 'var(--accent-blue)', fontWeight: '600', fontSize: '13px', marginBottom: '24px' }}>
            <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--accent-blue)' }}></span>
            The Modular School OS
          </div>
          <h1 className="spx-title-large" style={{ maxWidth: '800px', margin: '0 auto 24px auto' }}>
            Education Intelligence Platform powering modern schools
          </h1>
          <p className="spx-body-lead" style={{ maxWidth: '640px', margin: '0 auto 40px auto' }}>
            Unify your data silos to reveal hidden patterns in assessment, attendance, and fees. Boost student outcomes across your campuses with absolute clarity.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <a href="/onboarding" className="spx-btn spx-btn-primary">
              Start Free Pilot <i className="fas fa-arrow-right"></i>
            </a>
            <Link href="/contact" className="spx-btn spx-btn-secondary">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Core Integrity Pillars */}
      <section className="spx-section" style={{ borderTop: '1px solid var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}>
        <div className="spx-container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="spx-title-medium">Built for enterprise integrity</h2>
            <p className="spx-body-lead" style={{ maxWidth: '600px', margin: '0 auto' }}>
              SchoolPlex isn't just software; it's the operational bedrock that ensures your school runs with precision.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '32px' }}>
            <div className="spx-glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--accent-blue-light)', color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifySelf: 'start', justifyContent: 'center', fontSize: '20px' }}>
                <i className="fas fa-compass"></i>
              </div>
              <h3 className="spx-title-small" style={{ margin: 0 }}>Clarity Over Complexity</h3>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '15px' }}>
                Start with one module and move at your own pace, without disrupting your term or overwhelming staff.
              </p>
            </div>

            <div className="spx-glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--accent-blue-light)', color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifySelf: 'start', justifyContent: 'center', fontSize: '20px' }}>
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="spx-title-small" style={{ margin: 0 }}>Accountability by Default</h3>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '15px' }}>
                Track results, payments, and approvals in one place so every decision has a clear audit trail.
              </p>
            </div>

            <div className="spx-glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--accent-blue-light)', color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifySelf: 'start', justifyContent: 'center', fontSize: '20px' }}>
                <i className="fas fa-scale-balanced"></i>
              </div>
              <h3 className="spx-title-small" style={{ margin: 0 }}>Fair Modular Pricing</h3>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '15px' }}>
                No per-student billing multipliers. Pay for school volume bands, and activate only what you use.
              </p>
            </div>

            <div className="spx-glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--accent-blue-light)', color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifySelf: 'start', justifyContent: 'center', fontSize: '20px' }}>
                <i className="fas fa-wifi"></i>
              </div>
              <h3 className="spx-title-small" style={{ margin: 0 }}>Offline-First Reliability</h3>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '15px' }}>
                Critical modules like CBT run locally, protecting you from poor internet connectivity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Modules Slider */}
      <section className="spx-section" id="features">
        <div className="spx-container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="spx-title-medium">Start with what matters most</h2>
            <p className="spx-body-lead" style={{ maxWidth: '600px', margin: '0 auto' }}>
              Solve your school's immediate bottleneck this term, then activate additional modules when ready.
            </p>
          </div>

          <div className="spx-slider-container">
            {/* Sidebar Tab Selectors */}
            <div className="spx-slider-nav">
              {slides.map((slide, idx) => (
                <button
                  key={slide.id}
                  type="button"
                  className={`spx-slider-tab ${activeSlide === idx ? 'active' : ''}`}
                  onClick={() => setActiveSlide(idx)}
                >
                  <span className="spx-slider-tab-title">{slide.title}</span>
                </button>
              ))}
            </div>

            {/* Slider Content View */}
            <div className="spx-slider-view spx-glass-card">
              <h3 className="spx-title-medium" style={{ fontSize: '32px', marginBottom: '16px' }}>
                {selectedSolution.title}
              </h3>
              <p style={{ fontSize: '16px', color: 'var(--text-secondary)', marginBottom: '24px', fontWeight: '500' }}>
                {selectedSolution.tagline}
              </p>
              
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedSolution.points.map((pt, index) => (
                  <li key={index} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', color: 'var(--text-secondary)' }}>
                    <i className="fas fa-check-circle" style={{ color: 'var(--accent-blue)', marginTop: '4px' }}></i>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a href={selectedSolution.startLink} className="spx-btn spx-btn-primary">
                  Start Free <i className="fas fa-arrow-right"></i>
                </a>
                <Link href={selectedSolution.learnLink} className="spx-btn spx-btn-secondary">
                  Learn more
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Institution Types */}
      <section className="spx-section spx-institutions-section" style={{ borderTop: '1px solid var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}>
        <div className="spx-container">
          <div style={{ marginBottom: '48px', maxWidth: '720px' }}>
            <h2 className="spx-title-medium">Built for every academic model</h2>
            <p className="spx-body-lead" style={{ margin: 0 }}>
              SchoolPlex adapts to the governance, curriculum, billing, and reporting structure of each institution type.
            </p>
          </div>

          <div className="spx-institution-grid">
            {institutionTypes.map((type) => (
              <article className="spx-institution-card" key={type.code}>
                <img
                  className="spx-institution-image"
                  src={institutionImages[type.code] || '/images/sections/originals/academics_1547200287.jpg'}
                  alt=""
                  loading="lazy"
                />
                <h3 className="spx-institution-title">For {type.name}</h3>
                <p className="spx-institution-copy">{type.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="spx-section">
        <div className="spx-container">
          <div className="spx-glass-card" style={{ textAlign: 'center', background: 'linear-gradient(135deg, var(--bg-secondary) 30%, var(--accent-blue-light) 100%)', padding: '64px 32px' }}>
            <h2 className="spx-title-medium" style={{ marginBottom: '16px' }}>Ready to reveal the hidden gem beneath your school data?</h2>
            <p className="spx-body-lead" style={{ maxWidth: '600px', margin: '0 auto 32px auto' }}>
              Join the growing list of schools processes results, fee calculations, and online testing on a platform designed for clarity.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="/onboarding" className="spx-btn spx-btn-primary">
                Try Free for 30 Days
              </a>
              <a href="https://wa.me/2348144676666" target="_blank" rel="noopener noreferrer" className="spx-btn spx-btn-secondary" style={{ backgroundColor: '#25d366', color: 'white', borderColor: '#25d366' }}>
                <i className="fab fa-whatsapp"></i> Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
